// Fund II Overview capital-deployment math. Pure + unit-tested so the bar, the
// per-deal blocks/tooltips, the Coinvest tally and the Excess overlay all derive
// from one place. See docs/plans/fund-overview/reserve-cap-and-coinvest-spec.md.
import type { FundDeal, FundSettings } from '../types';

// Deal types that don't draw Fund II committed capital (add-ons use the
// portfolio company's existing reserve; ex-fund deals fall outside the mandate).
const EXCLUDED_DEAL_TYPES = new Set(['add-on', 'new (ex-fund)']);

export type ReservationMode = 'absolute' | 'weighted';

export interface DealBlock {
  key: string; // stable unique id (crm_list_entry.id) — for React keys; names can collide
  name: string;
  total: number; // est_investment, $mm
  fund: number; // min(total, cap) * p — contribution to Fund II reserve
  coinvest: number; // max(0, total - cap) * p — raised alongside the fund
}

export interface FundBreakdown {
  committed: number;
  invested: number;
  called: number;
  reservedExp: number;
  reservedLive: number; // Σ dealBlocks.fund
  coinvestTotal: number; // Σ dealBlocks.coinvest
  totalAllocated: number; // invested + called + reservedExp + reservedLive (coinvest is outside the fund)
  available: number; // max(0, committed - totalAllocated)
  excess: number; // max(0, totalAllocated - committed) — rendered as a red overlay, not a block
  dealBlocks: DealBlock[]; // contributing deals, largest-first
}

// `parseMoneyMM` lived here and went with the post-Affinity S2 repoint. It existed to interpret v1's
// `est_investment` DISPLAY STRING ("$20M", "~1.2B", "350.0") off the frozen Affinity snapshot. The
// native payload carries `est_investment_mm` as a typed number in $mm, so there is no string left to
// parse — and a parser kept "just in case" is a second definition of what a deal is worth.

const round1 = (n: number): number => +n.toFixed(1);

export function computeFundBreakdown(
  deals: FundDeal[],
  settings: FundSettings,
  mode: ReservationMode = 'absolute',
  stageProb: Record<string, number> = {}, // stage_value → pct (Phase 2; weighted mode)
): FundBreakdown {
  const { committed_capital_mm: committed, invested_mm: invested, called_expenses_mm: called, reserved_expenses_mm: reservedExp, per_deal_reserve_cap_mm: cap } = settings;

  const dealBlocks: DealBlock[] = [];
  // De-dup by entry id (the DEAL, not the company): a deal must be counted once
  // even if it surfaces in more than one input list (e.g. the weighted set
  // unions live + origination).
  const seen = new Set<number>();
  for (const d of deals) {
    const entryId = d.entry_id;
    if (entryId != null) {
      if (seen.has(entryId)) continue;
      seen.add(entryId);
    }
    const t = (d.deal_type || '').toLowerCase();
    if (EXCLUDED_DEAL_TYPES.has(t)) continue;
    const total = d.est_investment_mm || 0;
    if (total <= 0) continue;
    // Probability: 1.0 in absolute mode; P(stage) in weighted mode (Phase 2).
    const p = mode === 'weighted' ? (stageProb[d.stage] ?? 0) / 100 : 1;
    if (p <= 0) continue;
    // Structural-then-weighted: cap reflects the actual check; p scales each bucket.
    const fund = Math.round(Math.min(total, cap) * p);
    const coinvest = Math.round(Math.max(0, total - cap) * p);
    if (fund === 0 && coinvest === 0) continue;
    dealBlocks.push({
      // Identify by the DEAL (the list entry), not the company/org — matches how
      // the pipeline lists deals (DealTable renders deal.name).
      key: String(entryId ?? d.name),
      name: d.name,
      total: Math.round(total),
      fund,
      coinvest,
    });
  }
  dealBlocks.sort((a, b) => b.total - a.total);

  const reservedLive = dealBlocks.reduce((s, b) => s + b.fund, 0);
  const coinvestTotal = dealBlocks.reduce((s, b) => s + b.coinvest, 0);
  const totalAllocated = round1(invested + called + reservedExp + reservedLive);
  const available = Math.max(0, round1(committed - totalAllocated));
  const excess = Math.max(0, round1(totalAllocated - committed));

  return { committed, invested, called, reservedExp, reservedLive, coinvestTotal, totalAllocated, available, excess, dealBlocks };
}

// Total bar width in $mm for a breakdown: the committed line (or the
// over-allocated stack, whichever is larger) plus coinvest. Taking the max of
// this across modes gives a shared horizontal scale, so flipping
// Absolute/Weighted keeps $-per-pixel constant.
export function trackWidth(f: FundBreakdown): number {
  return Math.max(f.committed, f.totalAllocated) + f.coinvestTotal;
}
